import React from "react";
import { Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import SoupKitchenRoundedIcon from "@mui/icons-material/SoupKitchenRounded";
import LocalLaundryServiceRoundedIcon from "@mui/icons-material/LocalLaundryServiceRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Link } from "react-router-dom";

import Search from "../../Components/Search/Search";
import WorkerData from "../../Types/WorkerData";
import { Permissions, getPermissionKeys } from "../../Types/Enums/Permissions";
import { Functions, getFunctionKeys } from "../../Types/Enums/Functions";
import { AuthContext, AuthContextType } from "../../Types/AuthContext";
import WorkerRepository from "../../Repositories/WorkerRepository";
import WorkerGetResponse from "../../Repositories/WorkerResponses/WorkerGetResponse";
import LocationRepository from "../../Repositories/LocationRepository";
import NewUserDialog from "../../Components/Dialogs/NewUserDialog";
import Spinner from "../../Components/Spinner/Spinner";
import AuthRepository from "../../Repositories/AuthRepository";

type RegistrationFilter = "Any" | "Registered" | "Unregistered";
type SortOrder = "Name A-Z" | "Name Z-A" | "Role" | "Function" | "Registered first";

interface State {
    isLoading: boolean;
    workers: WorkerData[];
    query: string;
    permissions: Array<keyof typeof Permissions>;
    functions: Array<keyof typeof Functions>;
    registration: RegistrationFilter;
    sortOrder: SortOrder;
    hoveredId: string | null;
    showDeleteUserDialogue: boolean;
    showAddUserDialog: boolean;
    showRegisterUserDialog: boolean;
    showEditNameDialog: boolean;
    showEditFunctionDialog: boolean;
    selectedWorker: WorkerData | null;
    showUserDetails: boolean;
    errorOpen: boolean;
    errorTitle: string;
    errorMsg: string;
}

export default class UsersPage extends React.Component<{}, State> {
    static contextType = AuthContext;
    context!: AuthContextType;

    private readonly ACCENT = "#c79656";
    private readonly ACCENT_LIGHT = "rgba(199,150,86,0.10)";
    private readonly ACCENT_GLOW = "rgba(199,150,86,0.22)";
    private readonly SURFACE = "#ffffff";
    private readonly BORDER = "rgba(0,0,0,0.07)";
    private readonly TEXT_PRIMARY = "#18181b";
    private readonly TEXT_SECONDARY = "rgba(24,24,27,0.52)";

    constructor(props: {}) {
        super(props);

        this.onWorkerDeleteClick = this.onWorkerDeleteClick.bind(this);
        this.onWorkerAddClick = this.onWorkerAddClick.bind(this);
        this.onUserCreateClick = this.onUserCreateClick.bind(this);
        this.onEditNameClick = this.onEditNameClick.bind(this);
        this.onEditFunctionClick = this.onEditFunctionClick.bind(this);
        this.refreshUsers = this.refreshUsers.bind(this);
        this.fillUsernames = this.fillUsernames.bind(this);

        this.state = {
            isLoading: true,
            workers: [],
            query: "",
            permissions: [],
            functions: getFunctionKeys() as Array<keyof typeof Functions>,
            registration: "Any",
            sortOrder: "Name A-Z",
            hoveredId: null,
            showDeleteUserDialogue: false,
            showAddUserDialog: false,
            showRegisterUserDialog: false,
            showEditNameDialog: false,
            showEditFunctionDialog: false,
            selectedWorker: null,
            showUserDetails: false,
            errorOpen: false,
            errorTitle: "",
            errorMsg: "",
        };
    }

    componentDidMount() {
        this.refreshUsers();
    }

    private async refreshUsers() {
        const result = await WorkerRepository.getAllWorkers(this.context.locationId);
        if (result.isFailed()) return;

        const loaded = result.result!.response.map((x: WorkerGetResponse) =>
            new WorkerData(x.id, x.userId, x.firstName, x.lastName, x.debt, 0, x.permissions, x.function, 0, 0)
        ) as WorkerData[];
        this.setState({ workers: loaded, isLoading: false }, () => {
            this.fillUsernames();
        });
    }

    private async fillUsernames() {
        const result = await LocationRepository.getUsernames(this.context.locationId);
        if (result.isFailed()) {
            this.setState({
                errorOpen: true,
                errorTitle: result.errorTitle ?? "Unexpected error",
                errorMsg: result.errorMessage ?? "An unexpected error occured",
            });
        }

        this.setState((x) => ({
            workers: x.workers.slice().map((y) => workerWithUsername(y, result.result?.users[y.Guid] ?? "")),
        }));

        const workerWithUsername = (worker: WorkerData, username: string) => {
            worker.Username = username;
            return worker;
        };
    }

    private async onWorkerDeleteClick(accepted: boolean) {
        const w = this.state.selectedWorker;

        if (accepted && w) {
            const result = await WorkerRepository.deleteWorker(this.context.locationId, w.Guid);
            if (result.isFailed()) return;

            this.setState((prev) => ({
                showDeleteUserDialogue: false,
                selectedWorker: null,
                workers: prev.workers.filter((x) => x.Guid !== w.Guid),
            }));
        } else {
            this.setState({ showDeleteUserDialogue: false, selectedWorker: null });
        }
    }

    private async onWorkerAddClick(worker: WorkerData | null) {
        if (worker !== null) {
            const result = await WorkerRepository.createWorker(this.context.locationId, worker);
            if (result.isFailed()) return;

            worker.Guid = result.result?.id as string;

            this.setState((prev) => ({
                showAddUserDialog: false,
                selectedWorker: null,
                workers: [...prev.workers, worker],
            }));
        } else {
            this.setState({ showAddUserDialog: false, selectedWorker: null });
        }
    }

    private async onUserCreateClick(data: { username: string; password: string } | null) {
        if (data !== null) {
            const result = await AuthRepository.signup(
                data.username,
                data.password,
                this.state.selectedWorker?.Guid!,
                "",
                this.context.locationId
            );
            if (result.isFailed()) return;

            const _worker = this.state.selectedWorker;
            _worker!.Username = data.username;

            this.setState(() => ({
                showRegisterUserDialog: false,
                selectedWorker: null,
            }));
        } else {
            this.setState({ showRegisterUserDialog: false, selectedWorker: null });
        }
    }

    private async onEditNameClick(data: { firstName: string; lastName: string } | null) {
    }

    private async onEditFunctionClick(data: { function: Functions } | null) {
    }

    private async onUserDetailsClick(worker: WorkerData) {
        this.setState({
            selectedWorker: worker,
            showUserDetails: true,
        });
    }

    private async onUserDetailsClose() {
        this.setState({
            selectedWorker: null,
            showUserDetails: false,
        });
    }

    private functionMeta(f: Functions) {
        switch (f) {
            case Functions.Server:
                return { label: "Server", Icon: RestaurantRoundedIcon };
            case Functions.Kitchen:
                return { label: "Kitchen", Icon: SoupKitchenRoundedIcon };
            case Functions.Dishwashing:
                return { label: "Dishwashing", Icon: LocalLaundryServiceRoundedIcon };
            default:
                return { label: "Unknown", Icon: RestaurantRoundedIcon };
        }
    }

    private permissionMeta(p: Permissions) {
        type Tone = "neutral" | "accent" | "dark";
        switch (p) {
            case Permissions.User:
                return { label: "User", Icon: PersonRoundedIcon, tone: "neutral" as Tone };
            case Permissions.Administrator:
                return { label: "Admin", Icon: AdminPanelSettingsRoundedIcon, tone: "accent" as Tone };
            case Permissions.Developer:
                return { label: "Developer", Icon: CodeRoundedIcon, tone: "dark" as Tone };
            default:
                return { label: "Unknown", Icon: PersonRoundedIcon, tone: "neutral" as Tone };
        }
    }

    private permPillStyle(tone: "neutral" | "accent" | "dark"): React.CSSProperties {
        const tones = {
            neutral: {
                bg: "rgba(0,0,0,0.05)",
                fg: "rgba(24,24,27,0.72)",
                bd: "1px solid rgba(0,0,0,0.08)",
                icon: "rgba(24,24,27,0.45)",
            },
            accent: {
                bg: this.ACCENT_LIGHT,
                fg: "#a0722a",
                bd: `1px solid rgba(199,150,86,0.28)`,
                icon: this.ACCENT,
            },
            dark: {
                bg: "rgba(24,24,27,0.90)",
                fg: "#fff",
                bd: "1px solid rgba(255,255,255,0.08)",
                icon: "rgba(255,255,255,0.65)",
            },
        }[tone];

        return {
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "3px 8px 3px 5px",
            borderRadius: 5,
            background: tones.bg,
            color: tones.fg,
            border: tones.bd,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
            letterSpacing: 0.1,
            ["--icon-color" as any]: tones.icon,
        };
    }

    private applyFiltersAndSorting() {
        const { workers, query, permissions, functions, registration, sortOrder } = this.state;
        const q = query.trim().toLowerCase();

        let list = workers.slice();

        if (permissions.length > 0) {
            const permVals = permissions.map((p) => Permissions[p]);
            list = list.filter((w) => permVals.includes(w.Permission));
        }
        if (functions.length > 0) {
            const fnVals = functions.map((f) => Functions[f]);
            list = list.filter((w) => fnVals.includes(w.Function));
        }
        if (registration !== "Any") {
            const wantReg = registration === "Registered";
            list = list.filter((w) => !!w.Username === wantReg);
        }
        if (q) {
            list = list.filter((w) =>
                [w.FirstName, w.LastName, w.Username]
                    .filter(Boolean)
                    .some((v) => (v || "").toLowerCase().includes(q))
            );
        }

        list.sort((a, b) => {
            const an = `${a.FirstName} ${a.LastName}`.toLowerCase();
            const bn = `${b.FirstName} ${b.LastName}`.toLowerCase();
            switch (sortOrder) {
                case "Name A-Z":
                    return an.localeCompare(bn);
                case "Name Z-A":
                    return bn.localeCompare(an);
                case "Role":
                    return a.Permission - b.Permission || an.localeCompare(bn);
                case "Function":
                    return a.Function - b.Function || an.localeCompare(bn);
                case "Registered first":
                    return (b.Username ? 1 : 0) - (a.Username ? 1 : 0) || an.localeCompare(bn);
                default:
                    return 0;
            }
        });

        return list;
    }

    private resetFilters = () =>
        this.setState({
            query: "",
            permissions: [],
            functions: getFunctionKeys() as Array<keyof typeof Functions>,
            registration: "Any",
            sortOrder: "Name A-Z",
        });

    private buildGroupedItems(list: WorkerData[]) {
        const { functions, permissions, sortOrder } = this.state;
        const byFunction = functions.length > 0 || sortOrder === "Function";
        const byPermission = !byFunction && (permissions.length > 0 || sortOrder === "Role");

        if (!byFunction && !byPermission) {
            return list.map((u) => ({ type: "user", user: u } as const));
        }

        if (byFunction) {
            const orderIds = (
                functions.length > 0 ? functions : (getFunctionKeys() as Array<keyof typeof Functions>)
            ).map((k) => Functions[k]) as number[];
            const groups = new Map<number, WorkerData[]>();
            for (const id of orderIds) groups.set(id, []);
            for (const u of list) {
                const id = u.Function as number;
                if (!groups.has(id)) groups.set(id, []);
                groups.get(id)!.push(u);
            }
            const items: Array<{ type: "section"; title: string; count: number } | { type: "user"; user: WorkerData }> = [];
            for (const id of orderIds) {
                const arr = groups.get(id) || [];
                if (arr.length === 0) continue;
                const title = this.functionMeta(id as Functions).label;
                items.push({ type: "section", title, count: arr.length });
                const sorted = arr.slice().sort((a, b) => {
                    const an = `${a.FirstName} ${a.LastName}`.toLowerCase();
                    const bn = `${b.FirstName} ${b.LastName}`.toLowerCase();
                    return sortOrder === "Name Z-A" ? bn.localeCompare(an) : an.localeCompare(bn);
                });
                for (const u of sorted) items.push({ type: "user", user: u });
            }
            return items;
        }

        const permOrderIds = (
            permissions.length > 0 ? permissions : (getPermissionKeys() as Array<keyof typeof Permissions>)
        ).map((k) => Permissions[k]) as number[];
        const permGroups = new Map<number, WorkerData[]>();
        for (const id of permOrderIds) permGroups.set(id, []);
        for (const u of list) {
            const id = u.Permission as number;
            if (!permGroups.has(id)) permGroups.set(id, []);
            permGroups.get(id)!.push(u);
        }
        const items: Array<{ type: "section"; title: string; count: number } | { type: "user"; user: WorkerData }> = [];
        for (const id of permOrderIds) {
            const arr = permGroups.get(id) || [];
            if (arr.length === 0) continue;
            const title = this.permissionMeta(id as Permissions).label;
            items.push({ type: "section", title, count: arr.length });
            const sorted = arr.slice().sort((a, b) => {
                const an = `${a.FirstName} ${a.LastName}`.toLowerCase();
                const bn = `${b.FirstName} ${b.LastName}`.toLowerCase();
                return sortOrder === "Name Z-A" ? bn.localeCompare(an) : an.localeCompare(bn);
            });
            for (const u of sorted) items.push({ type: "user", user: u });
        }
        return items;
    }

    private Pill = ({
        active,
        onClick,
        children,
        ariaLabel,
    }: {
        active?: boolean;
        onClick?: () => void;
        children: React.ReactNode;
        ariaLabel?: string;
    }) => (
        <button
            type="button"
            aria-pressed={active}
            aria-label={ariaLabel}
            onClick={onClick}
            style={{
                appearance: "none",
                border: `1.5px solid ${active ? this.ACCENT : "rgba(0,0,0,0.12)"}`,
                background: active ? this.ACCENT_LIGHT : "transparent",
                color: active ? "#7a5420" : this.TEXT_SECONDARY,
                padding: "5px 11px",
                borderRadius: 7,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.15,
                cursor: "pointer",
                transition: "all 120ms ease",
                lineHeight: 1.4,
            }}
            onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                if (!active) el.style.borderColor = "rgba(199,150,86,0.5)";
                el.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLButtonElement;
                el.style.borderColor = active ? this.ACCENT : "rgba(0,0,0,0.12)";
                el.style.transform = "translateY(0)";
            }}
        >
            {children}
        </button>
    );

    private Segmented = ({
        value,
        onChange,
        options,
    }: {
        value: string;
        onChange: (next: string) => void;
        options: string[];
    }) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {options.map((opt) => (
                <this.Pill key={opt} active={value === opt} onClick={() => onChange(opt)} ariaLabel={opt}>
                    {opt}
                </this.Pill>
            ))}
        </div>
    );

    private MultiPills = <T extends string,>({
        selected,
        onToggle,
        options,
    }: {
        selected: T[];
        onToggle: (opt: T) => void;
        options: T[];
    }) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {options.map((opt) => {
                const active = selected.includes(opt);
                return (
                    <this.Pill key={opt} active={active} onClick={() => onToggle(opt)} ariaLabel={opt}>
                        {opt}
                    </this.Pill>
                );
            })}
        </div>
    );

    private SidebarSection = ({
        label,
        icon,
        children,
    }: {
        label: string;
        icon?: React.ReactNode;
        children: React.ReactNode;
    }) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: this.TEXT_SECONDARY,
                }}
            >
                {icon && (
                    <span style={{ display: "flex", alignItems: "center", color: "rgba(199,150,86,0.7)" }}>
                        {icon}
                    </span>
                )}
                {label}
            </div>
            {children}
        </div>
    );

    private SectionDivider = ({ title, count }: { title: string; count: number }) => (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "14px 20px 6px 20px",
            }}
        >
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 0.9,
                    textTransform: "uppercase",
                    color: this.TEXT_SECONDARY,
                    whiteSpace: "nowrap",
                }}
            >
                {title}
            </span>
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 18,
                    height: 16,
                    padding: "0 5px",
                    borderRadius: 4,
                    background: this.ACCENT_LIGHT,
                    color: "#9a6f2e",
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: 1,
                }}
            >
                {count}
            </span>
            <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.07)" }} />
        </div>
    );

    render() {
        const {
            workers,
            isLoading: loading,
            permissions,
            functions,
            registration,
            sortOrder,
            hoveredId,
        } = this.state;
        const filtered = this.applyFiltersAndSorting();
        const items = this.buildGroupedItems(filtered);
        const registeredCount = workers.filter((w) => !!w.Username).length;

        return (
            <div
                style={{
                    width: "100%",
                    height: "100%",
                    boxSizing: "border-box",
                    padding: "20px 24px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    gap: 20,
                    background: this.SURFACE,
                    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
                }}
            >
                {this.state.isLoading && <Spinner />}

                <div
                    style={{
                        width: "100%",
                        maxWidth: 1600,
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                    }}
                >
                    <div>
                        <div
                            style={{
                                fontSize: 26,
                                fontWeight: 800,
                                lineHeight: 1.1,
                                color: this.TEXT_PRIMARY,
                                letterSpacing: -0.5,
                            }}
                        >
                            Users
                        </div>
                        <div style={{ fontSize: 13, color: this.TEXT_SECONDARY, marginTop: 3, fontWeight: 400 }}>
                            {loading ? "Loading…" : `${workers.length} members · ${registeredCount} registered`}
                        </div>
                    </div>

                    <div style={{ flex: 1 }} />

                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<AddIcon style={{ fontSize: 17 }} />}
                        onClick={() => this.setState({ showAddUserDialog: true })}
                        style={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: 13,
                            borderRadius: 9,
                            paddingInline: 16,
                            paddingBlock: 8,
                            backgroundColor: this.ACCENT,
                            boxShadow: `0 2px 8px ${this.ACCENT_GLOW}, 0 1px 2px rgba(0,0,0,0.1)`,
                            letterSpacing: 0.1,
                        }}
                    >
                        Add user
                    </Button>
                </div>

                <div style={{ flex: 1, minHeight: 0 }}>
                    <div
                        style={{
                            width: "100%",
                            maxWidth: 1600,
                            margin: "0 auto",
                            height: "100%",
                            display: "grid",
                            gridTemplateColumns: "1fr 300px",
                            gap: 16,
                            alignItems: "start",
                        }}
                    >
                        <div
                            style={{
                                background: this.SURFACE,
                                borderRadius: 14,
                                border: `1px solid ${this.BORDER}`,
                                boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)",
                                overflow: "clip",
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                minHeight: 0,
                            }}
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "2fr 1fr 1fr 32px",
                                    padding: "9px 20px",
                                    borderBottom: `1px solid ${this.BORDER}`,
                                    background: "rgba(0,0,0,0.018)",
                                    fontSize: 10,
                                    fontWeight: 700,
                                    letterSpacing: 0.8,
                                    textTransform: "uppercase",
                                    color: this.TEXT_SECONDARY,
                                    flexShrink: 0,
                                }}
                            >
                                <div>Name</div>
                                <div>Function</div>
                                <div>Permission</div>
                                <div style={{ textAlign: "right" }}>Open</div>
                            </div>

                            <div
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    overscrollBehavior: "contain",
                                }}
                            >
                                {loading && (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 48,
                                            color: this.TEXT_SECONDARY,
                                            fontSize: 13,
                                        }}
                                    >
                                        Loading users…
                                    </div>
                                )}

                                {!loading && filtered.length === 0 && (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 56,
                                            gap: 8,
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: "50%",
                                                background: "rgba(0,0,0,0.04)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <PersonRoundedIcon style={{ fontSize: 19, color: "rgba(0,0,0,0.22)" }} />
                                        </div>
                                        <div style={{ fontSize: 13.5, fontWeight: 600, color: "rgba(0,0,0,0.38)" }}>
                                            No users match your filters
                                        </div>
                                        <button
                                            type="button"
                                            onClick={this.resetFilters}
                                            style={{
                                                appearance: "none",
                                                background: "transparent",
                                                border: "none",
                                                color: this.ACCENT,
                                                fontWeight: 600,
                                                fontSize: 12,
                                                cursor: "pointer",
                                                padding: 0,
                                            }}
                                        >
                                            Clear filters
                                        </button>
                                    </div>
                                )}

                                {!loading &&
                                    items.map((it, idx) => {
                                        if (it.type === "section") {
                                            return (
                                                <this.SectionDivider
                                                    key={`sec-${idx}`}
                                                    title={it.title}
                                                    count={it.count}
                                                />
                                            );
                                        }

                                        const u = it.user;
                                        const hovered = hoveredId === u.Guid;
                                        const func = this.functionMeta(u.Function);
                                        const perm = this.permissionMeta(u.Permission);
                                        const initials = `${u.FirstName.charAt(0)}${u.LastName.charAt(0)}`.toUpperCase();
                                        const isRegistered = !!u.Username;

                                        return (
                                            <Link
                                                key={u.Guid}
                                                to={`/workerdetails?workerId=${encodeURIComponent(u.Guid)}`}
                                                onMouseEnter={() => this.setState({ hoveredId: u.Guid })}
                                                onMouseLeave={() => this.setState({ hoveredId: null })}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "2fr 1fr 1fr 32px",
                                                    alignItems: "center",
                                                    padding: "6px 20px",
                                                    borderBottom: `1px solid ${this.BORDER}`,
                                                    background: hovered ? "rgba(199,150,86,0.03)" : this.SURFACE,
                                                    transition: "background 120ms ease, transform 120ms ease",
                                                    minHeight: 40,
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                aria-label={`${u.FirstName} ${u.LastName}`}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 10,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: 7,
                                                            background: this.ACCENT_LIGHT,
                                                            color: "#9a6f2e",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 10.5,
                                                            fontWeight: 750,
                                                            flexShrink: 0,
                                                            letterSpacing: 0.3,
                                                            border: `1px solid rgba(199,150,86,0.18)`,
                                                        }}
                                                    >
                                                        {initials}
                                                    </div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div
                                                            style={{
                                                                fontWeight: 650,
                                                                fontSize: 13,
                                                                lineHeight: 1.25,
                                                                color: this.TEXT_PRIMARY,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {u.FirstName} {u.LastName}
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontSize: 11,
                                                                lineHeight: 1.2,
                                                                marginTop: 1,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {isRegistered ? (
                                                                <span style={{ color: this.TEXT_SECONDARY }}>
                                                                    @{u.Username}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: "rgba(0,0,0,0.28)" }}>
                                                                    No linked account
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div>
                                                    <div
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 4,
                                                            padding: "3px 8px 3px 5px",
                                                            borderRadius: 5,
                                                            border: `1px solid rgba(199,150,86,0.25)`,
                                                            background: this.ACCENT_LIGHT,
                                                            color: "#8a5e1a",
                                                            fontWeight: 600,
                                                            fontSize: 11,
                                                            lineHeight: 1,
                                                            whiteSpace: "nowrap",
                                                        }}
                                                        aria-label={`Function: ${func.label}`}
                                                    >
                                                        <func.Icon style={{ fontSize: 12, color: this.ACCENT }} />
                                                        <span>{func.label}</span>
                                                    </div>
                                                </div>

                                                <div>
                                                    <div
                                                        style={this.permPillStyle(perm.tone)}
                                                        aria-label={`Permission: ${perm.label}`}
                                                    >
                                                        <perm.Icon
                                                            style={{ fontSize: 12, color: "var(--icon-color)" as any }}
                                                        />
                                                        <span>{perm.label}</span>
                                                    </div>
                                                </div>

                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        color: hovered ? this.ACCENT : "rgba(0,0,0,0.22)",
                                                        transform: hovered ? "translateX(1px)" : "translateX(0)",
                                                        transition: "color 120ms ease, transform 120ms ease",
                                                    }}
                                                >
                                                    <ChevronRightRoundedIcon style={{ fontSize: 18 }} />
                                                </div>
                                            </Link>
                                        );
                                    })}
                            </div>

                            <div
                                style={{
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "8px 20px",
                                    borderTop: `1px solid ${this.BORDER}`,
                                    background: "rgba(0,0,0,0.012)",
                                    gap: 6,
                                    fontSize: 12,
                                    color: this.TEXT_SECONDARY,
                                    fontWeight: 500,
                                }}
                            >
                                <span>Showing</span>
                                <span
                                    style={{
                                        fontWeight: 700,
                                        color: this.TEXT_PRIMARY,
                                        background: "rgba(0,0,0,0.05)",
                                        borderRadius: 4,
                                        padding: "1px 6px",
                                        fontSize: 11,
                                    }}
                                >
                                    {loading ? "…" : `${filtered.length} of ${workers.length}`}
                                </span>
                            </div>
                        </div>

                        <div
                            style={{
                                background: this.SURFACE,
                                borderRadius: 14,
                                border: `1px solid ${this.BORDER}`,
                                boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)",
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    padding: "12px 18px",
                                    borderBottom: `1px solid ${this.BORDER}`,
                                    background: "rgba(0,0,0,0.018)",
                                }}
                            >
                                <FilterListRoundedIcon style={{ fontSize: 14, color: this.TEXT_SECONDARY }} />
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: this.TEXT_PRIMARY,
                                        letterSpacing: 0.1,
                                    }}
                                >
                                    Filters
                                </span>
                                <div style={{ flex: 1 }} />
                                <button
                                    type="button"
                                    onClick={this.resetFilters}
                                    style={{
                                        appearance: "none",
                                        background: "transparent",
                                        border: "none",
                                        color: this.ACCENT,
                                        fontWeight: 700,
                                        fontSize: 11,
                                        cursor: "pointer",
                                        padding: 0,
                                        letterSpacing: 0.1,
                                    }}
                                >
                                    Reset
                                </button>
                            </div>

                            <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 18 }}>
                                <Search sx={{ width: "100%" }} onChange={(v) => this.setState({ query: v ?? "" })} />

                                <this.SidebarSection label="Registration">
                                    <this.Segmented
                                        value={registration}
                                        onChange={(next) => this.setState({ registration: next as RegistrationFilter })}
                                        options={["Any", "Registered", "Unregistered"]}
                                    />
                                </this.SidebarSection>

                                <this.SidebarSection label="Permission">
                                    <this.MultiPills
                                        selected={permissions}
                                        onToggle={(opt) => {
                                            const has = permissions.includes(opt);
                                            this.setState({
                                                permissions: has
                                                    ? permissions.filter((p) => p !== opt)
                                                    : [...permissions, opt],
                                            });
                                        }}
                                        options={getPermissionKeys() as Array<keyof typeof Permissions>}
                                    />
                                </this.SidebarSection>

                                <this.SidebarSection label="Function">
                                    <this.MultiPills
                                        selected={functions}
                                        onToggle={(opt) => {
                                            const has = functions.includes(opt);
                                            this.setState({
                                                functions: has
                                                    ? functions.filter((f) => f !== opt)
                                                    : [...functions, opt],
                                            });
                                        }}
                                        options={getFunctionKeys() as Array<keyof typeof Functions>}
                                    />
                                </this.SidebarSection>

                                <div style={{ width: "100%", height: 1, background: this.BORDER }} />

                                <this.SidebarSection
                                    label="Sort by"
                                    icon={<SortRoundedIcon style={{ fontSize: 13 }} />}
                                >
                                    <this.Segmented
                                        value={sortOrder}
                                        onChange={(next) => this.setState({ sortOrder: next as SortOrder })}
                                        options={["Name A-Z", "Name Z-A", "Role", "Function", "Registered first"]}
                                    />
                                </this.SidebarSection>
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.showAddUserDialog && <NewUserDialog onClose={this.onWorkerAddClick} />}

                {/* {this.state.showEditNameDialog && (
                    <EditNameDialog
                        worker={this.state.selectedWorker!}
                        onClose={this.onEditNameClick}
                    />
                )}

                {this.state.showEditFunctionDialog && (
                    <EditFunctionDialog
                        worker={this.state.selectedWorker!}
                        onClose={this.onEditFunctionClick}
                    />
                )} */}
            </div>
        );
    }
}
